import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Navigate, Route, Routes, useParams } from "react-router-dom";
import { Box } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { MotionConfig } from "framer-motion";
import Navbar from "./Navbar";
import Home from "./Home";
import Footer from "./Footer";
import { LanguageProvider, useLanguage } from "./context/LanguageContext";
import { ImageModeProvider } from "./context/ImageModeContext";
import LanguageRouteSync from "./components/LanguageRouteSync";
import ScrollToTop from "./components/ScrollToTop";
import RouteMetadata from "./components/RouteMetadata";
import StickyBookCta from "./components/StickyBookCta";
import { getServiceById, getServiceIds, getServicePath, resolveServiceId } from "./services/catalog";
import theme from "./theme";
import "./App.css";

// Code-split non-critical routes. Home stays eager because "/" redirects to
// "/no" (Home) and is the most frequent landing page. Footer/Navbar stay
// eager because they render on every page.
const Services = lazy(() => import("./Services"));
const ServiceDetail = lazy(() => import("./ServiceDetail"));
const About = lazy(() => import("./About"));
const Contact = lazy(() => import("./Contact"));
const Projects = lazy(() => import("./Projects"));
const BookingPage = lazy(() => import("./BookingPage"));
const Privacy = lazy(() => import("./Privacy"));
const ProductsPage = lazy(() => import("./ProductsPage"));
const NotFound = lazy(() => import("./NotFound"));

function ServicePage({ serviceId }) {
  return <ServiceDetail serviceIdOverride={serviceId} />;
}

function ServiceRedirect({ targetLanguage, serviceIdOverride }) {
  const { serviceId: routeServiceId } = useParams();
  const resolvedServiceId = resolveServiceId(serviceIdOverride ?? routeServiceId);
  const fallback = targetLanguage === "en" ? "/en/services" : "/no/services";

  return <Navigate to={resolvedServiceId ? getServicePath(resolvedServiceId, targetLanguage) : fallback} replace />;
}

const skipLinkCopy = {
  no: "Hopp til hovedinnhold",
  en: "Skip to main content",
};

const skipLinkSx = {
  position: "absolute",
  top: 8,
  left: 8,
  zIndex: 2000,
  px: 2,
  py: 1,
  borderRadius: 1,
  backgroundColor: "#0F172A",
  color: "#FFFFFF",
  fontWeight: 600,
  textDecoration: "none",
  transform: "translateY(-200%)",
  transition: "transform 0.15s ease",
  "&:focus, &:focus-visible": {
    transform: "translateY(0)",
    outline: "2px solid #7BD8D5",
    outlineOffset: 2,
  },
};

function AppShell() {
  const { language } = useLanguage();
  const serviceIds = getServiceIds();
  const skipLabel = skipLinkCopy[language] ?? skipLinkCopy.en;

  return (
    <Router>
      <div className="App">
        <LanguageRouteSync />
        <ScrollToTop />
        <RouteMetadata language={language} />
        <Box component="a" href="#main-content" sx={skipLinkSx}>
          {skipLabel}
        </Box>
        <Navbar />
        <Box
          component="main"
          id="main-content"
          tabIndex={-1}
          // Match MUI's default Toolbar min-heights so the fixed AppBar never
          // covers content below: 56px on xs, 64px on sm and up.
          sx={{ mt: { xs: "56px", sm: "64px" }, outline: "none" }}
        >
          <Suspense fallback={<Box sx={{ minHeight: "60vh", width: "100%", backgroundColor: "background.paper" }} />}>
          <Routes>
            <Route path="/" element={<Navigate to="/no" replace />} />

            <Route path="/no" element={<Home />} />
            <Route path="/no/services" element={<Services />} />
            <Route path="/no/services/:serviceId" element={<ServiceDetail />} />
            <Route path="/no/projects" element={<Navigate to="/no/case" replace />} />
            <Route path="/no/case" element={<Projects />} />
            <Route path="/no/about" element={<Navigate to="/no/om" replace />} />
            <Route path="/no/om" element={<About />} />
            <Route path="/no/contact" element={<Navigate to="/no/kontakt" replace />} />
            <Route path="/no/kontakt" element={<Contact />} />
            <Route path="/no/book" element={<BookingPage />} />
            <Route path="/no/privacy" element={<Navigate to="/no/personvern" replace />} />
            <Route path="/no/personvern" element={<Privacy />} />
            <Route path="/no/produkter" element={<ProductsPage />} />

            {serviceIds.map((serviceId) => (
              <Route
                key={`no-${serviceId}`}
                path={getServicePath(serviceId, "no")}
                element={<ServicePage serviceId={serviceId} />}
              />
            ))}

            <Route path="/en" element={<Home />} />
            <Route path="/en/services" element={<Services />} />
            <Route path="/en/services/:serviceId" element={<ServiceDetail />} />
            <Route path="/en/projects" element={<Navigate to="/en/case" replace />} />
            <Route path="/en/case" element={<Projects />} />
            <Route path="/en/about" element={<Navigate to="/en/om" replace />} />
            <Route path="/en/om" element={<About />} />
            <Route path="/en/contact" element={<Navigate to="/en/kontakt" replace />} />
            <Route path="/en/kontakt" element={<Contact />} />
            <Route path="/en/book" element={<BookingPage />} />
            <Route path="/en/privacy" element={<Navigate to="/en/personvern" replace />} />
            <Route path="/en/personvern" element={<Privacy />} />
            <Route path="/en/products" element={<ProductsPage />} />

            {serviceIds.map((serviceId) => (
              <Route
                key={`en-${serviceId}`}
                path={getServicePath(serviceId, "en")}
                element={<ServicePage serviceId={serviceId} />}
              />
            ))}

            <Route path="/services" element={<Navigate to="/no/services" replace />} />
            <Route path="/services/:serviceId" element={<ServiceRedirect targetLanguage="no" />} />
            <Route path="/projects" element={<Navigate to="/no/case" replace />} />
            <Route path="/case" element={<Navigate to="/no/case" replace />} />
            <Route path="/about" element={<Navigate to="/no/om" replace />} />
            <Route path="/om" element={<Navigate to="/no/om" replace />} />
            <Route path="/contact" element={<Navigate to="/no/kontakt" replace />} />
            <Route path="/kontakt" element={<Navigate to="/no/kontakt" replace />} />
            <Route path="/book" element={<Navigate to="/no/book" replace />} />
            <Route path="/privacy" element={<Navigate to="/no/personvern" replace />} />
            <Route path="/personvern" element={<Navigate to="/no/personvern" replace />} />
            <Route path="/produkter" element={<Navigate to="/no/produkter" replace />} />
            <Route path="/products" element={<Navigate to="/no/produkter" replace />} />

            {serviceIds.map((serviceId) => {
              const service = getServiceById(serviceId);
              return (
                <React.Fragment key={`legacy-${serviceId}`}>
                  <Route path={`/${service.slugs.no}`} element={<ServiceRedirect targetLanguage="no" serviceIdOverride={serviceId} />} />
                  <Route path={`/${service.slugs.en}`} element={<ServiceRedirect targetLanguage="no" serviceIdOverride={serviceId} />} />
                </React.Fragment>
              );
            })}

            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </Box>
        <StickyBookCta />
        <Footer />
      </div>
    </Router>
  );
}

function App() {
  return (
    <LanguageProvider>
      <ImageModeProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {/* reducedMotion="user" makes framer-motion honor the OS prefers-reduced-motion
              setting site-wide: transform/movement animations are disabled for opted-in
              users (safe opacity fades remain), while everyone else sees the full
              animations unchanged. Completes the CSS reduced-motion reset in index.css. */}
          <MotionConfig reducedMotion="user">
            <AppShell />
          </MotionConfig>
        </ThemeProvider>
      </ImageModeProvider>
    </LanguageProvider>
  );
}

export default App;
